/**
 * Utility functions for working with trip data
 */

import { getPartnerPlan } from '../config/plans';
import { canUsePartnerAiPlanning } from './partnerTierGates';

/**
 * Parse a trip date string without timezone shifting.
 * @param {string} dateStr
 * @returns {Date|null}
 */
export const parseTripDate = (dateStr) => {
  if (!dateStr) return null;
  const datePart = dateStr.toString().split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

/**
 * Get timeline status for a trip relative to today.
 * @param {Object} trip
 * @param {Date} [now]
 * @returns {'upcoming'|'current'|'past'|'unknown'}
 */
export const getTripTimelineStatus = (trip, now = new Date()) => {
  const startDate = parseTripDate(trip?.start_date);
  const endDate = parseTripDate(trip?.end_date);
  if (!startDate || !endDate) return 'unknown';

  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  if (today < start) return 'upcoming';
  if (today > end) return 'past';
  return 'current';
};

/**
 * Active trips are upcoming or currently in progress (not completed).
 * @param {Object} trip
 * @param {Date} [now]
 * @returns {boolean}
 */
export const isActiveTrip = (trip, now = new Date()) => getTripTimelineStatus(trip, now) !== 'past';

/** Completed trips are read-only: no add/edit and no Suggest with AI. */
export const canEditTripItinerary = (trip, now = new Date()) => isActiveTrip(trip, now);

/**
 * Sort trips by start date.
 * @param {Array} trips
 * @param {'asc'|'desc'} [direction='asc']
 * @returns {Array}
 */
export const sortTripsByStartDate = (trips, direction = 'asc') => {
  const sorted = [...trips].sort((a, b) => {
    const aTime = parseTripDate(a.start_date)?.getTime() ?? 0;
    const bTime = parseTripDate(b.start_date)?.getTime() ?? 0;
    return aTime - bTime;
  });
  return direction === 'desc' ? sorted.reverse() : sorted;
};

/**
 * Creator-owned trips that are still active, sorted by start date ascending.
 * @param {Array} trips
 * @param {Date} [now]
 * @returns {Array}
 */
export const getActiveCreatorTrips = (trips, now = new Date()) => sortTripsByStartDate(
  (trips || []).filter((trip) => trip.user_role === 'creator' && isActiveTrip(trip, now))
);

/**
 * Case-insensitive match against what a traveler sees on a trip card.
 * @param {Object} trip
 * @param {string} query
 * @returns {boolean}
 */
export const tripMatchesSearch = (trip, query) => {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    trip?.title,
    trip?.master_trip_title,
    trip?.description,
    trip?.trip_type,
    trip?.location,
    trip?.creator_name,
    trip?.partner_company_name,
    trip?.trip_code,
    ...getTripLocationNames(trip),
  ];
  return haystack.some((value) => String(value || '').toLowerCase().includes(needle));
};

export const COMING_UP_WINDOW_MONTHS = 3;

/**
 * Trips in progress or starting within the next few months.
 * @param {Object} trip
 * @param {Date} [now]
 * @param {number} [months]
 * @returns {boolean}
 */
export const isTripComingUp = (trip, now = new Date(), months = COMING_UP_WINDOW_MONTHS) => {
  const status = getTripTimelineStatus(trip, now);
  if (status === 'current') return true;
  if (status !== 'upcoming') return false;
  const windowEnd = new Date(now);
  windowEnd.setHours(23, 59, 59, 999);
  windowEnd.setMonth(windowEnd.getMonth() + months);
  return parseTripDate(trip.start_date) <= windowEnd;
};

/**
 * My Trips sections: coming up (soonest first), then the rest split by
 * trips the user created and trips they were invited to.
 * @param {Array} trips
 * @param {Date} [now]
 * @returns {{ comingUp: Array, created: Array, invited: Array }}
 */
export const groupMyTripsSections = (trips, now = new Date()) => {
  const comingUp = [];
  const created = [];
  const invited = [];
  (trips || []).forEach((trip) => {
    if (isTripComingUp(trip, now)) comingUp.push(trip);
    else if (trip.user_role === 'creator') created.push(trip);
    else invited.push(trip);
  });
  return { comingUp: sortTripsByStartDate(comingUp), created, invited };
};

export const isUsableTripLocationName = (name) => {
  const text = String(name || '').trim();
  if (!text) return false;
  // Bare numbers / coords (e.g. "00", "0.00", "51.5, 0") are not place names.
  if (/^[\d.+,\-\s]+$/.test(text)) return false;
  return true;
};

export const getTripLocationNames = (trip) => {
  const fromArray = Array.isArray(trip?.locations)
    ? trip.locations.map((loc) => loc?.name).filter(isUsableTripLocationName)
    : [];
  if (fromArray.length) return [...new Set(fromArray)];
  if (isUsableTripLocationName(trip?.location)) return [String(trip.location).trim()];
  if (isUsableTripLocationName(trip?.first_location)) return [String(trip.first_location).trim()];
  return [];
};

/**
 * Get the primary location from a trip object
 * @param {Object} trip - The trip object
 * @returns {string} - The primary location name, or fallback text
 */
export const getTripPrimaryLocation = (trip) => {
  const names = getTripLocationNames(trip);
  return names[0] || 'No destination set';
};

/**
 * Get all location names from a trip object
 * @param {Object} trip - The trip object
 * @returns {string} - Comma-separated list of location names
 */
export const getTripAllLocations = (trip) => {
  const names = getTripLocationNames(trip);
  return names.length ? names.join(', ') : 'No destination set';
};

/**
 * Get trip locations array for components that need the full array
 * @param {Object} trip - The trip object
 * @returns {Array} - Array of location objects
 */
export const getTripLocationsArray = (trip) => {
  if (!trip) return [];

  if (Array.isArray(trip.locations)) {
    return trip.locations.filter((loc) => isUsableTripLocationName(loc?.name));
  }

  if (isUsableTripLocationName(trip.location)) {
    return [{ name: String(trip.location).trim(), coordinates: null }];
  }

  return [];
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const getBookedPackageCode = (trip) => {
  const match = trip?.description?.match(/^Booked package:\s*(.+)$/i);
  return match ? match[1].trim() : null;
};

export const getTripCreatorLabel = (trip) => {
  const named = [trip?.creator_first_name, trip?.creator_last_name]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
  if (named) return named;
  if (trip?.creator_email) return trip.creator_email;
  return trip?.creator_name || '';
};

const MANAGED_TRIP_HIDDEN_TABS = new Set(['map']);

export const filterTripTabs = (tabs, { isPartner = false, managedByPartner = false, canViewHistory = false } = {}) => (
  (tabs || []).filter((tab) => {
    if (isPartner && !tab.partner) return false;
    if (tab.ownerOnly && !canViewHistory) return false;
    if (managedByPartner && MANAGED_TRIP_HIDDEN_TABS.has(tab.key)) return false;
    if (tab.managedOnly && !managedByPartner) return false;
    return true;
  })
);

export const isManagedByPartner = (trip) => {
  const value = trip?.managed_by_partner;
  return value === true || value === 1 || value === '1';
};

export const isBespokePartnerTrip = (trip, entitlements = null) => {
  if (entitlements && String(entitlements.tier || '').toLowerCase() === 'bespoke') {
    return true;
  }
  return String(trip?.branding?.tier || trip?.partner_plan_tier || '').toLowerCase() === 'bespoke';
};

export const partnerTripIncludesAiPlanning = (trip, entitlements = null) => {
  if (entitlements) return canUsePartnerAiPlanning(entitlements);
  const tier = String(trip?.branding?.tier || trip?.partner_plan_tier || '').toLowerCase();
  if (!tier) return false;
  return Boolean(getPartnerPlan(tier)?.hasAiPlanning);
};

/**
 * Paid add-ons and Trip Pass tabs are included on Bespoke. Hide the "not free"
 * badge only then — buying a pass does not change that the feature is paid.
 */
export const showPaidFeatureBadge = (trip, { entitlements } = {}) => !isBespokePartnerTrip(trip, entitlements);
export const showPaidTripPassBadge = showPaidFeatureBadge;

/** Show PRO on partner AI only when the plan does not include AI planning. */
export const showPaidAiBadge = (trip, { entitlements } = {}) => {
  if (entitlements || isManagedByPartner(trip)) {
    return !partnerTripIncludesAiPlanning(trip, entitlements);
  }
  return true;
};

export const canShowTripAiGenerate = (trip) => !isManagedByPartner(trip);

export const canShowTripSuggestWithAi = (trip, { isCreator = false, isPartner = false } = {}) => {
  if (!canEditTripItinerary(trip)) return false;
  if (isManagedByPartner(trip)) return Boolean(isCreator || isPartner);
  return Boolean(isCreator);
};

/** The group trip itself, or a traveler's trip inside one. */
export const isGroupTrip = (trip) => Boolean(
  trip?.is_master_trip || trip?.is_child_trip || trip?.master_trip_id
);

/**
 * A traveler's trip inside a group trip carries the group's details, which
 * only the organizer changes from the group trip itself.
 */
export const canEditTripDetails = (trip, { isCreator = false, isPartner = false } = {}) => {
  if (!isCreator && !isPartner) return false;
  return !isGroupTrip(trip) || Boolean(trip?.is_master_trip);
};

export const canShowTripBookedBy = (trip) => {
  if (!isManagedByPartner(trip)) return false;
  const name = String(trip?.partner_company_name || '').trim();
  if (!name) return false;
  // Numeric leftovers like "00" are not an agency name.
  return isUsableTripLocationName(name);
};

export const getDisplayTripTitle = (trip) => {
  // A traveler's copy of a group trip is shown under the group trip's title.
  const groupTitle = trip?.master_trip_id ? (trip?.master_trip_title || '').trim() : '';
  const title = groupTitle || (trip?.title || '').trim();
  if (!title) return '';
  const code = getBookedPackageCode(trip);
  if (code) {
    const stripped = title.replace(new RegExp(`\\s*[-–—]\\s*${escapeRegExp(code)}\\s*$`, 'i'), '').trim();
    if (stripped) return stripped;
  }
  const dvnMatch = title.match(/^(.*)\s*[-–—]\s*(DVN-[A-Z0-9]+)\s*$/i);
  if (dvnMatch?.[1]?.trim()) return dvnMatch[1].trim();
  return title;
};

